import {
  PDFArray,
  PDFDict,
  PDFDocument,
  PDFName,
  PDFPage,
  PDFRef,
  PDFStream,
} from 'pdf-lib';
import type { OutputGroup } from './types';

export interface BuildResult {
  /** Output file name -> PDF bytes. */
  files: Map<string, Uint8Array>;
  /** `unresolved-pages.pdf` bytes, when there are unresolved pages. */
  unresolvedPdf: Uint8Array | null;
}

/**
 * Build the output PDFs by copying the original pages from the source PDF.
 * The original page content, size and quality are preserved exactly; only
 * page selection/ordering changes.
 */
export async function buildOutputPdfs(
  sourcePdfBytes: Uint8Array,
  outputs: OutputGroup[],
  unresolvedPages: number[],
): Promise<BuildResult> {
  const source = await PDFDocument.load(sourcePdfBytes, { ignoreEncryption: true });
  const files = new Map<string, Uint8Array>();
  let unresolvedPdf: Uint8Array | null = null;

  for (const group of outputs) {
    if (group.pageNumbers.length === 0) continue;
    const doc = await PDFDocument.create();
    const copied = await doc.copyPages(
      source,
      group.pageNumbers.map((p) => p - 1),
    );
    await pruneAllPageResources(doc, copied);
    for (const page of copied) doc.addPage(page);
    files.set(group.fileName, await doc.save());
  }

  if (unresolvedPages.length > 0) {
    const doc = await PDFDocument.create();
    const copied = await doc.copyPages(
      source,
      unresolvedPages.map((p) => p - 1),
    );
    await pruneAllPageResources(doc, copied);
    for (const page of copied) doc.addPage(page);
    unresolvedPdf = await doc.save();
  }

  return { files, unresolvedPdf };
}

/**
 * Some PDF generators share one giant resource dictionary across every page
 * (e.g. every page references all document images). pdf-lib's copyPages
 * faithfully copies that dictionary, which can bloat a one-page output to
 * the size of the whole source file.
 *
 * This helper parses the page's content stream and keeps only the resources
 * that are actually referenced by the operators (images via `Do`, fonts via
 * `Tf`, graphics states via `gs`, shading/patterns/color spaces, and the
 * marked-content property list). Everything else is pruned.
 */
/**
 * Prune the resource dictionaries of all copied pages in an output document
 * and delete objects that are no longer referenced from the context so
 * pdf-lib does not serialize them.
 */
async function pruneAllPageResources(doc: PDFDocument, pages: PDFPage[]): Promise<void> {
  const allUsed = new Set<string>();
  const usedPerPage: Array<Set<string> | null> = [];
  for (const page of pages) {
    const used = await collectUsedResourceNames(page);
    usedPerPage.push(used);
    if (used) {
      for (const name of used) allUsed.add(name);
    }
  }

  const originalResourceRefs: PDFRef[] = [];
  for (let i = 0; i < pages.length; i += 1) {
    const page = pages[i];
    const used = usedPerPage[i];
    if (!used) continue; // unparseable content -> keep all resources
    const resourcesRef = page.node.get(PDFName.of('Resources'));
    if (resourcesRef instanceof PDFRef) originalResourceRefs.push(resourcesRef);
    await prunePageResources(page, allUsed);
  }

  // Delete objects from the original resources that are no longer used by
  // any page, so they are not serialized into the output file.
  if (usedPerPage.every((u) => u)) {
    deleteUnusedResources(doc, originalResourceRefs, allUsed);
  }
}

async function prunePageResources(page: PDFPage, allUsed: Set<string>): Promise<void> {
  const context = page.doc.context;
  const resourcesRef = page.node.get(PDFName.of('Resources'));
  const resources = resourcesRef instanceof PDFRef
    ? context.lookup(resourcesRef)
    : resourcesRef;
  if (!(resources instanceof PDFDict)) return;

  const newResources = context.obj({});
  const sections: Array<[string, string]> = [
    ['XObject', 'XObject'],
    ['Font', 'Font'],
    ['ExtGState', 'ExtGState'],
    ['Pattern', 'Pattern'],
    ['Shading', 'Shading'],
    ['ColorSpace', 'ColorSpace'],
  ];
  for (const [, sectionName] of sections) {
    const section = resources.get(PDFName.of(sectionName));
    const sectionDict =
      section instanceof PDFRef ? context.lookup(section) : section;
    if (!(sectionDict instanceof PDFDict)) continue;
    const newSection = context.obj({});
    let kept = 0;
    for (const name of allUsed) {
      const entry = sectionDict.get(PDFName.of(name));
      if (entry) {
        newSection.set(PDFName.of(name), entry);
        kept += 1;
      }
    }
    if (kept > 0) newResources.set(PDFName.of(sectionName), newSection);
  }

  // Marked-content property lists are small; keep them as-is.
  for (const key of ['ProcSet', 'Properties']) {
    const entry = resources.get(PDFName.of(key));
    if (entry) newResources.set(PDFName.of(key), entry);
  }

  if (newResources.keys().length > 0) {
    page.node.set(PDFName.of('Resources'), newResources);
  }
}

function deleteUnusedResources(
  doc: PDFDocument,
  originalResourceRefs: PDFRef[],
  allUsed: Set<string>,
): void {
  const context = doc.context;
  const handled = new Set<string>();
  for (const resRef of originalResourceRefs) {
    if (handled.has(resRef.toString())) continue;
    handled.add(resRef.toString());
    const resources = context.lookup(resRef);
    if (!(resources instanceof PDFDict)) {
      try {
        context.delete(resRef);
      } catch {
        /* ignore */
      }
      continue;
    }
    for (const sectionName of ['XObject', 'Font', 'ExtGState', 'Pattern', 'Shading', 'ColorSpace']) {
      const sectionEntry = resources.get(PDFName.of(sectionName));
      const sectionRef = sectionEntry instanceof PDFRef ? sectionEntry : null;
      if (sectionRef && handled.has(sectionRef.toString())) continue;
      const section = sectionRef ? context.lookup(sectionRef) : sectionEntry;
      if (!(section instanceof PDFDict)) continue;
      for (const name of section.keys()) {
        if (allUsed.has(name.decodeText())) continue;
        const entry = section.get(name);
        if (entry == null) continue;
        const entryRef = entry instanceof PDFRef ? entry : context.getObjectRef(entry);
        if (entryRef && !handled.has(entryRef.toString())) {
          handled.add(entryRef.toString());
          try {
            context.delete(entryRef);
          } catch {
            /* ignore */
          }
        }
      }
      if (sectionRef) {
        handled.add(sectionRef.toString());
        try {
          context.delete(sectionRef);
        } catch {
          /* ignore */
        }
      }
    }
    try {
      context.delete(resRef);
    } catch {
      /* ignore */
    }
  }
}

async function collectUsedResourceNames(page: PDFPage): Promise<Set<string> | null> {
  const used = new Set<string>();
  const contentsRef = page.node.get(PDFName.of('Contents'));
  const contents =
    contentsRef instanceof PDFRef ? page.doc.context.lookup(contentsRef) : contentsRef;
  if (!contents) return used;
  const streams: PDFStream[] = [];
  if (contents instanceof PDFStream) {
    streams.push(contents);
  } else if (contents instanceof PDFArray) {
    for (const item of contents.asArray()) {
      const resolved =
        item instanceof PDFRef ? page.doc.context.lookup(item) : item;
      if (resolved instanceof PDFStream) streams.push(resolved);
    }
  }

  for (const stream of streams) {
    try {
      const content = stream.getContentsString();
      if (!content) continue;
      const names = extractUsedResourceNames(content);
      if (names.size === 0) {
        // Some generators write compressed content streams without a
        // recognized filter; pdf-lib then exposes the raw bytes. Inflate
        // and retry before falling back to keeping all resources.
        try {
          const inflated = await inflateContent(content);
          for (const name of extractUsedResourceNames(inflated)) {
            names.add(name);
          }
        } catch {
          /* not compressed or undecodable */
        }
      }
      for (const name of names) used.add(name);
    } catch {
      // If the content stream cannot be parsed, keep all resources intact
      // rather than risk corrupting the page.
      return null;
    }
  }
  return used;
}

function extractUsedResourceNames(content: string): Set<string> {
  const used = new Set<string>();
  // Look for `/Name <operator>` patterns. The operators that reference page
  // resources are: Do (XObject), Tf (font), gs (graphics state), sh (shading)
  // and cs/CS/scn/SCN (color space).
  const re = /\/([A-Za-z0-9][A-Za-z0-9._-]*)\s+(?:Do|Tf|gs|sh|cs|CS|scn|SCN)\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    used.add(m[1]);
  }
  return used;
}

async function inflateContent(content: string): Promise<string> {
  const bytes = new Uint8Array(content.length);
  for (let i = 0; i < content.length; i += 1) {
    bytes[i] = content.charCodeAt(i) & 0xff;
  }
  const stream = new Blob([bytes as unknown as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate'));
  const inflated = await new Response(stream).arrayBuffer();
  let text = '';
  const view = new Uint8Array(inflated);
  for (let i = 0; i < view.length; i += 1) {
    text += String.fromCharCode(view[i]);
  }
  return text;
}
